<template>
  <div
    class="p-4 max-w-7xl mx-auto pt-10 dark:bg-gray-900 transition-colors duration-200"
  >
    <div v-if="loading" class="text-center dark:text-gray-200">
      Loading model...
    </div>

    <div v-else-if="error" class="text-center">
      <div class="text-red-600 dark:text-red-400 mb-4">{{ error }}</div>
      <button class="btn" @click="$router.push({ name: 'overview' })">
        Back to Overview
      </button>
    </div>

    <template v-else-if="model">
      <section class="flex flex-col justify-between md:flex-row">
        <div class="flex items-center gap-4">
          <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {{ model.name }}
          </h1>
          <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
            :class="getStatusClass(model.status)"
          >
            {{ getStatusDisplay(model.status) }}
          </span>
        </div>
      </section>

      <section class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-5">
        <div class="h-32 bg-white dark:bg-gray-800 card">
          <div class="text-5xl text-gray-900 dark:text-gray-100">
            {{
              model.metrics?.samples >= 1000000
                ? (model.metrics?.samples / 1000000).toFixed(2) + "M"
                : model.metrics?.samples || "-"
            }}
          </div>
          <div class="text-gray-600 dark:text-gray-400">samples</div>
        </div>
        <div class="h-32 bg-white dark:bg-gray-800 card">
          <div class="text-5xl text-gray-900 dark:text-gray-100">
            {{ model.metrics?.metric?.value?.toFixed(2) || "-" }}
          </div>
          <div class="text-gray-600 dark:text-gray-400">
            {{ model.metrics?.metric?.name || "-" }}
          </div>
        </div>
        <div class="h-32 bg-white dark:bg-gray-800 card relative">
          <div
            class="h-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-4 py-2 rounded-full inline-flex items-center justify-center font-medium text-sm absolute top-3 right-3"
          >
            WIP
          </div>
          <div class="text-5xl text-gray-900 dark:text-gray-100">
            7
            <span class="text-2xl text-gray-600 dark:text-gray-400">/ 8</span>
          </div>
          <div class="text-gray-600 dark:text-gray-400">
            base learners active
          </div>
        </div>
        <div class="h-32 bg-white dark:bg-gray-800 card relative">
          <div
            class="h-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-4 py-2 rounded-full inline-flex items-center justify-center font-medium text-sm absolute top-3 right-3"
          >
            WIP
          </div>
          <div class="text-5xl text-gray-900 dark:text-gray-100">
            4
            <span class="text-2xl text-gray-600 dark:text-gray-400">/ 5</span>
          </div>
          <div class="text-gray-600 dark:text-gray-400">
            meta-feature categories
          </div>
        </div>

        <div class="h-32 bg-white dark:bg-gray-800 card relative">
          <div
            class="h-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-4 py-2 rounded-full inline-flex items-center justify-center font-medium text-sm absolute top-3 right-3"
          >
            WIP
          </div>
          <div class="text-5xl text-gray-900 dark:text-gray-100">0</div>
          <div class="text-gray-600 dark:text-gray-400">
            meta-features extracted
          </div>
        </div>
      </section>

      <section class="my-4">
        <div class="flex flex-col bg-white dark:bg-gray-800 card min-h-96">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Checkpoints
            </h2>
          </div>

          <div
            v-if="checkpoints.length === 0"
            class="flex items-center justify-center h-full text-gray-500 dark:text-gray-400"
          >
            <p>No checkpoints recorded yet</p>
          </div>

          <article
            v-for="checkpoint in checkpoints"
            :key="checkpoint.id"
            class="checkpoint"
          >
            <div
              class="flex items-center justify-between py-3 cursor-pointer"
              @click="checkpoint.expanded = !checkpoint.expanded"
            >
              <div class="w-6 mr-4 text-gray-600 dark:text-gray-400">
                <Icon v-if="checkpoint.expanded" :path="mdiChevronUp" />
                <Icon v-else :path="mdiChevronRight" />
              </div>

              <div class="flex-auto text-gray-900 dark:text-gray-100">
                {{ checkpoint.id }}
              </div>
            </div>

            <transition enter-class="opacity-0" leave-class="opacity-0">
              <div
                v-if="checkpoint.expanded"
                class="py-2 duration-200 border-t border-gray-200 dark:border-gray-700"
              >
                <code
                  class="p-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded"
                  >{{ checkpoint }}</code
                >
              </div>
            </transition>
          </article>

          <div
            class="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400"
          >
            <p>
              Creating, restoring, and deleting checkpoints is currently only
              available via the API. Please refer to the
              <a
                href="https://docs.kappaml.com/reference/api/models#get-model-checkpoints"
                target="_blank"
                class="underline"
                >API documentation</a
              >
              to use this feature.
            </p>
          </div>
        </div>
      </section>

      <section class="grid grid-cols-1 gap-4 mt-4">
        <div class="bg-white dark:bg-gray-800 card">
          <div class="flex items-center justify-between mb-4">
            <div class="font-semibold text-gray-900 dark:text-gray-100">
              Aggregated model activity over time
            </div>
            <select
              v-model="selectedTimeRange"
              class="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
              @change="fetchHistoricalMetrics"
            >
              <option value="5m">Last 5 minutes</option>
              <option value="15m">Last 15 minutes</option>
              <option value="30m">Last 30 minutes</option>
              <option value="1h">Last hour</option>
              <option value="24h">Last day</option>
              <option value="7d">Last week</option>
              <option value="30d">Last month</option>
              <option value="90d">Last 3 months</option>
              <option value="180d">Last 6 months</option>
              <option value="365d">Last 12 months</option>
            </select>
          </div>
          <div
            v-if="loadingHistoricalMetrics"
            class="flex justify-center items-center h-64"
          >
            <div class="text-gray-600 dark:text-gray-400">
              Loading metrics...
            </div>
          </div>
          <div
            v-else-if="historicalMetricsError"
            class="flex justify-center items-center h-64"
          >
            <div class="text-red-600 dark:text-red-400">
              {{ historicalMetricsError }}
            </div>
          </div>
          <ActivityLineChart v-else :chart-data="activityChartData" />
        </div>
      </section>

      <section
        class="pt-8 my-8 text-center border-t border-gray-200 dark:border-gray-700"
      >
        <div
          class="font-semibold cursor-pointer text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center justify-center gap-1"
          @click="showDeleteModal = true"
        >
          <Icon :path="mdiTrashCan" class="w-5 h-5" />
          Delete model
        </div>
      </section>
    </template>

    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" title="Delete Model">
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        Are you sure you want to delete this model? This action cannot be
        undone.
      </p>
      <template #footer>
        <button
          class="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          @click="showDeleteModal = false"
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          @click="deleteModel"
        >
          <Icon :path="mdiTrashCan" class="w-4 h-4" />
          Delete
        </button>
      </template>
    </Modal>

    <Playground :model="model" @update:model="model = $event" />
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { mdiChevronRight, mdiChevronUp, mdiTrashCan } from "@mdi/js";
import kappamlAPI from "@/services/kappaml-api";
import Modal from "@/components/shared/Modal.vue";
import ActivityLineChart from "@/components/charts/ActivityLineChart.vue";
import Playground from "@/components/Playground.vue";

export default {
  components: {
    ActivityLineChart,
    Modal,
    Playground,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const model = ref(null);
    const user = ref(null);
    const checkpoints = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const showDeleteModal = ref(false);
    const instance = computed(() => route.query.instance || null);
    const selectedTimeRange = ref("24h");
    const historicalMetrics = ref(null);
    const loadingHistoricalMetrics = ref(false);
    const historicalMetricsError = ref(null);
    const pollingInterval = ref(null);

    watch(
      () => model.value?.name,
      (name) => {
        document.title = name ? `${name} - KappaML` : "KappaML";
      },
    );

    onMounted(() => {
      fetchModel(true);
      // Set up polling every 30 seconds without showing loading state
      pollingInterval.value = setInterval(() => fetchModel(false), 5000);
    });

    onUnmounted(() => {
      document.title = "KappaML";
      // Clean up the interval when component is unmounted
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
      }
    });

    const fetchMetrics = async (modelId) => {
      try {
        const metricsData = await kappamlAPI.getModelMetrics(modelId, instance.value);
        if (model.value) {
          model.value.metrics = metricsData;
        }
      } catch (err) {
        console.error("Error fetching metrics:", err);
      }
    };

    const fetchHistoricalMetrics = async () => {
      if (!model.value?.id) return;

      loadingHistoricalMetrics.value = true;
      historicalMetricsError.value = null;

      try {
        const data = await kappamlAPI.getHistoricalMetrics(
          model.value.id,
          selectedTimeRange.value,
          instance.value,
        );
        historicalMetrics.value = data;
      } catch (err) {
        console.error("Error fetching historical metrics:", err);
        historicalMetricsError.value = "Failed to load historical metrics";
      } finally {
        loadingHistoricalMetrics.value = false;
      }
    };

    const fetchModel = async (showLoading = true) => {
      if (showLoading) {
        loading.value = true;
      }
      error.value = null;

      try {
        const modelId = route.params.id;
        if (!modelId) {
          error.value = "No model ID provided";
          return;
        }

        const modelData = await kappamlAPI.getModel(modelId);
        // Preserve existing metrics to avoid UI flicker while fresh metrics load
        const previousMetrics = model.value?.metrics;
        model.value = {
          ...modelData,
          metrics: previousMetrics ?? modelData.metrics,
        };

        // Load metrics separately
        await fetchMetrics(modelId);
        await fetchCheckpoints(modelId);
        await fetchHistoricalMetrics();
      } catch (err) {
        console.error("Error fetching model:", err);
        error.value = "Error loading model";
      } finally {
        if (showLoading) {
          loading.value = false;
        }
      }
    };

    const fetchCheckpoints = async (modelId) => {
      try {
        const checkpointsData = await kappamlAPI.getModelCheckpoints(modelId);
        if (checkpointsData && checkpointsData.checkpoints) {
          checkpoints.value = checkpointsData.checkpoints;
        }
      } catch (err) {
        console.error("Error fetching checkpoints:", err);
      }
    };

    const deleteModel = async () => {
      try {
        await kappamlAPI.deleteModel(route.params.id);
        router.push({ name: "overview" });
      } catch (err) {
        console.error("Error deleting model:", err);
        alert("Failed to delete model");
      }
    };

    const getStatusClass = (status) => {
      const states = {
        PENDING: "Pending",
        DEPLOYING: "Deploying",
        DEPLOYED: "Deployed",
        FAILED: "Failed",
        DELETING: "Deleting",
      };

      const classes = {
        [states.PENDING]: "bg-yellow-100 text-yellow-800",
        [states.DEPLOYING]: "bg-blue-100 text-blue-800",
        [states.DEPLOYED]: "bg-green-100 text-green-800",
        [states.FAILED]: "bg-red-100 text-red-800",
        [states.DELETING]: "bg-gray-100 text-gray-800",
      };

      return classes[status] || classes[states.PENDING];
    };

    const getStatusDisplay = (status) => {
      return status || "Pending";
    };

    const activityChartData = computed(() => {
      if (
        !historicalMetrics.value ||
        !historicalMetrics.value.metric_name ||
        !historicalMetrics.value.data
      ) {
        return {
          labels: [],
          datasets: [],
        };
      }

      return {
        labels: historicalMetrics.value.data.map(({ timestamp }) =>
          new Date(timestamp).toLocaleString("en-US", {
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          }),
        ),
        datasets: [
          {
            label: historicalMetrics.value.metric_name,
            backgroundColor: "rgba(19,162,173, 0.2)",
            borderColor: "rgb(19,162,173)",
            data: historicalMetrics.value.data.map(
              ({ metric_value }) => metric_value,
            ),
            borderWidth: 2,
            tension: 0.3,
          },
        ],
      };
    });

    return {
      model,
      user,
      checkpoints,
      loading,
      error,
      showDeleteModal,
      mdiChevronRight,
      mdiChevronUp,
      mdiTrashCan,
      deleteModel,
      getStatusClass,
      getStatusDisplay,
      selectedTimeRange,
      loadingHistoricalMetrics,
      historicalMetricsError,
      activityChartData,
      fetchHistoricalMetrics,
    };
  },
  computed: {
    methodsRadarData() {
      return {
        labels: ["Method 1", "Method 2", "Method 3", "Method 4", "Method 5"],
        datasets: [
          {
            label: "Correctness",
            backgroundColor: "rgb(19,162,173, 0.5)",
            borderColor: "#13A2AD",
            pointBackgroundColor: "#13A2AD",
            data: [0.6, 0.7, 0, 0.6, 0.5],
          },
        ],
      };
    },
  },
};
</script>

<style lang="postcss" scoped>
.card {
  @apply p-6 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700;
}
.event {
  @apply p-4
  my-4
  bg-gray-800/50
  dark:bg-gray-800
  font-semibold
  shadow-lg
  rounded-lg
  cursor-pointer
  border
  border-gray-200
  dark:border-gray-700
  hover:bg-gray-700/50
  dark:hover:bg-gray-700
  transition-colors;
}
</style>
